import { useState, useEffect, useMemo, useCallback } from 'react';
import { useProductOffers } from './useProductOffers';
import { useDiscountTime } from './useDiscountTime';
import { sellerService } from '@/services/seller';
import { batchUpsertProductOffers, removeProductOffer } from '@/services/seller/discount/discount-offers';
import { upsertProductStock } from '@/services/seller/seller-helpers';
import { getKoreanDate } from '@/lib/dateUtils';
import { calculateOrderTimes } from '@/services/seller/discount/discount-time';
import { 
  DiscountProduct, 
  UseDiscountProductsOptions, 
  UseDiscountProductsReturn 
} from '@/types/discount';

export function useDiscountProducts({
  restaurantId,
  offerType,
  initialPickupStartTime = '18:00',
  initialPickupEndTime = '20:00'
}: UseDiscountProductsOptions): UseDiscountProductsReturn {
  // 상태 관리
  const [products, setProducts] = useState<DiscountProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Product Offers 훅
  const { 
    offers, 
    loading: offersLoading, 
    refetch: refetchOffers
  } = useProductOffers({ 
    restaurantId, 
    offerType 
  });

  // 시간 관리 훅
  const {
    pickupStartTime,
    pickupEndTime,
    setPickupStartTime,
    setPickupEndTime,
    isValidTimeRange
  } = useDiscountTime({
    initialPickupStartTime,
    initialPickupEndTime,
    offerType
  });

  // 초기 데이터 로드
  useEffect(() => {
    const loadProducts = async () => {
      if (!restaurantId || restaurantId.trim() === '') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);


        // 상품 목록 가져오기
        const productsResult = await sellerService.getSellerProducts(restaurantId);
        
        if (productsResult.success && productsResult.data) {
          
          // 상품과 offers 데이터 병합
          const mergedProducts: DiscountProduct[] = productsResult.data.map(product => {
            // 직접 등록 상태 확인
            const productOffer = offers.find(o => o.product_id === product.id);
            let isRegistered = false;
            
            if (productOffer && productOffer.is_current) {
              if (offerType === 'DEADLINE_DISCOUNT' && productOffer.valid_from) {
                const today = getKoreanDate();
                const offerDate = productOffer.valid_from.split('T')[0];
                isRegistered = offerDate === today;
              } else if (offerType === 'RESERVATION_DISCOUNT') {
                isRegistered = true;
              }
            }
            
            // 기본 할인율 설정
            let defaultDiscountRate = 40;
            if (offerType === 'DEADLINE_DISCOUNT') {
              defaultDiscountRate = product.name.includes('랜덤') ? 50 : 40;
            } else {
              defaultDiscountRate = 5; // 예약할인 기본값
            }

            const mergedProduct = {
              productId: product.id,
              name: product.name,
              originalPrice: product.original_price,
              discountRate: productOffer?.discount_rate || defaultDiscountRate,
              discountedPrice: productOffer?.discounted_price || 
                Math.floor(product.original_price * (100 - defaultDiscountRate) / 100),
              stock: productOffer ? (offerType === 'DEADLINE_DISCOUNT' ? 20 : 999) : (offerType === 'DEADLINE_DISCOUNT' ? 20 : 999),
              selected: isRegistered, // DB에 등록되어 있으면 선택된 상태로
              isRandomBox: product.name.includes('랜덤'),
              isRegisteredInDB: isRegistered,
              isModified: false,
              offerId: productOffer?.id,
              validFrom: productOffer?.valid_from,
              pickupStartTime: productOffer?.deadline_pickup_start_time,
              pickupEndTime: productOffer?.deadline_pickup_end_time
            };

            return mergedProduct;
          });

          
          setProducts(mergedProducts);

          // 등록된 상품의 시간 정보 적용
          const registeredProduct = mergedProducts.find(p => p.isRegisteredInDB);
          if (registeredProduct) {
            const startTime = registeredProduct.pickupStartTime;
            const endTime = registeredProduct.pickupEndTime;
            
            if (startTime && endTime && startTime !== '' && endTime !== '') {
              // 시간 형식을 HH:MM으로 통일 (HH:MM:SS → HH:MM)
              const formattedStartTime = startTime.substring(0, 5);
              const formattedEndTime = endTime.substring(0, 5);
              
              // 다음 렌더링 사이클에 적용
              setTimeout(() => {
                setPickupStartTime(formattedStartTime);
                setPickupEndTime(formattedEndTime);
              }, 0);
            }
          }
        }
      } catch (err) {
        console.error('Load products error:', err);
        setError('상품 목록을 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    // offers 데이터가 로드된 후에만 실행
    if (!offersLoading) {
      loadProducts();
    }
  }, [restaurantId, offers, offersLoading, offerType]);

  // 등록된 상품과 미등록 상품 분리
  const registeredProducts = useMemo(() => 
    products.filter(p => p.selected && p.isRegisteredInDB),
    [products]
  );

  const availableProducts = useMemo(() => 
    products.filter(p => !p.selected),
    [products]
  );

  // 상품 업데이트 함수들
  const updateProduct = useCallback((productId: string, updates: Partial<DiscountProduct>) => {
    setProducts(prev => prev.map(product => 
      product.productId === productId 
        ? { ...product, ...updates, isModified: true }
        : product
    ));
  }, []);

  const toggleProductSelection = useCallback((productId: string) => {
    setProducts(prev => prev.map(product => 
      product.productId === productId 
        ? { ...product, selected: !product.selected, isModified: true }
        : product
    ));
  }, []);

  const handleDiscountRateChange = useCallback((productId: string, discountRate: number) => {
    setProducts(prev => prev.map(product => 
      product.productId === productId 
        ? { 
            ...product, 
            discountRate,
            discountedPrice: Math.floor(product.originalPrice * (100 - discountRate) / 100),
            isModified: true
          }
        : product
    ));
  }, []);

  const handleStockChange = useCallback((productId: string, stock: number) => {
    setProducts(prev => prev.map(product => 
      product.productId === productId 
        ? { ...product, stock, isModified: true }
        : product
    ));
  }, []);

  const handlePriceChange = useCallback((productId: string, price: number) => {
    setProducts(prev => prev.map(product => 
      product.productId === productId && product.isRandomBox
        ? { 
            ...product, 
            originalPrice: price,
            discountedPrice: Math.floor(price * (100 - product.discountRate) / 100),
            isModified: true
          }
        : product
    ));
  }, []);

  // 전체 선택 함수들
  const selectAllRegistered = useCallback((selected: boolean) => {
    setProducts(prev => prev.map(product => 
      (product.isRegisteredInDB || product.selected)
        ? { ...product, selected, isModified: true }
        : product
    ));
  }, []);

  const selectAllAvailable = useCallback((selected: boolean) => {
    setProducts(prev => prev.map(product => 
      (!product.isRegisteredInDB && !product.selected)
        ? { ...product, selected, isModified: true }
        : product
    ));
  }, []);

  // 시간 업데이트
  const updatePickupTimes = useCallback((start: string, end: string) => {
    setPickupStartTime(start);
    setPickupEndTime(end);
  }, [setPickupStartTime, setPickupEndTime]);

  // 저장 함수
  const saveProducts = useCallback(async (): Promise<{ success: boolean; error?: string } | undefined> => {
    if (!restaurantId || restaurantId.trim() === '' || !isValidTimeRange) return;

    try {
      setSaving(true);
      setError(null);

      // 1️⃣ 체크 해제된 상품 찾기 (등록되어 있었는데 선택 해제된 것들)
      const productsToDelete = products.filter(p => p.isRegisteredInDB && !p.selected);
      
      // 2️⃣ 체크 해제된 상품들의 offer 삭제
      for (const product of productsToDelete) {
        const deleteResult = await removeProductOffer(product.productId, offerType);
        if (!deleteResult.success) {
          console.error(`Failed to remove offer for ${product.name}:`, deleteResult.error);
          // 에러가 있어도 계속 진행
        }
      }

      // 3️⃣ 선택된 상품들 처리 (기존 로직)
      const selectedProducts = products
        .filter(p => p.selected)
        .map(p => ({
          productId: p.productId,
          discountRate: p.discountRate,
          discountedPrice: p.discountedPrice,
          stock: p.stock,
          pickupStartTime: offerType === 'DEADLINE_DISCOUNT' ? pickupStartTime : undefined,
          pickupEndTime: offerType === 'DEADLINE_DISCOUNT' ? pickupEndTime : undefined
        }));

      // product_offers 일괄 저장
      const result = await batchUpsertProductOffers(selectedProducts, offerType, restaurantId);
      
      if (!result.success) {
        throw new Error(result.error);
      }

      // product_stock 업데이트 - 마감할인만 처리 (예약할인은 재고와 무관)
      if (offerType === 'DEADLINE_DISCOUNT') {
        const stockDate = getKoreanDate();
        for (const product of selectedProducts) {
          await upsertProductStock(product.productId, stockDate, product.stock);
        }
      }

      // offers 다시 불러오기
      await refetchOffers();

      // 4️⃣ 상태 업데이트 - 삭제된 상품의 isRegisteredInDB를 false로
      setProducts(prev => prev.map(product => {
        // 삭제된 상품들
        if (productsToDelete.some(p => p.productId === product.productId)) {
          return {
            ...product,
            isModified: false,
            isRegisteredInDB: false, // DB에서 삭제됨
            selected: false
          };
        }
        // 저장된 상품들
        else if (product.selected) {
          return {
            ...product,
            isModified: false,
            isRegisteredInDB: true // DB에 저장됨
          };
        }
        // 기타
        return {
          ...product,
          isModified: false
        };
      }));

      return { success: true };
    } catch (err) {
      console.error('Save products error:', err);
      setError('저장 중 오류가 발생했습니다');
      return { success: false, error: err instanceof Error ? err.message : '알 수 없는 오류' };
    } finally {
      setSaving(false);
    }
  }, [restaurantId, products, pickupStartTime, pickupEndTime, offerType, isValidTimeRange, refetchOffers]);

  // 통계
  const selectedCount = useMemo(() => 
    products.filter(p => p.selected).length,
    [products]
  );

  const canSave = useMemo(() => 
    selectedCount > 0 && isValidTimeRange && !saving,
    [selectedCount, isValidTimeRange, saving]
  );

  // 주문 시간 계산
  const orderTimes = useMemo(() => {
    if (offerType === 'DEADLINE_DISCOUNT') {
      return calculateOrderTimes(pickupStartTime, pickupEndTime);
    }
    return { orderStartTime: '', orderEndTime: '' };
  }, [pickupStartTime, pickupEndTime, offerType]);

  return {
    // 상품 데이터
    products,
    registeredProducts,
    availableProducts,
    
    // 로딩 상태
    loading: loading || offersLoading,
    saving,
    error,
    
    // 상품 조작
    updateProduct,
    toggleProductSelection,
    handleDiscountRateChange,
    handleStockChange,
    handlePriceChange,
    
    // 시간 설정
    pickupTimes: { start: pickupStartTime, end: pickupEndTime },
    orderStartTime: orderTimes.orderStartTime,
    orderEndTime: orderTimes.orderEndTime,
    updatePickupTimes,
    
    // 저장
    saveProducts,
    
    // 전체 선택
    selectAllRegistered,
    selectAllAvailable,
    
    // 통계
    selectedCount,
    canSave
  };
}